import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditDocumentIcon from '@mui/icons-material/EditDocument';
import PublishIcon from '@mui/icons-material/Publish';
import { alpha, useTheme } from '@mui/material/styles';
import {
    createClientdealFeasibilityComment,
    getClientdealDeal,
    getClientdealFeasibilityIssuedReportDownload,
    inferClientdealFeasibilityRisks,
    issueClientdealFeasibilityReport,
    launchClientdealFeasibilityCollabora,
    listClientdealDeals,
    queueClientdealFeasibilityGeneration,
    refreshClientdealFeasibilityCollaboraToken,
    updateClientdealFeasibilityFieldReview,
    updateClientdealFeasibilityReviewTarget,
    updateClientdealFeasibilityRisks,
    updateClientdealDealStatus
} from './api';

function formatDateTime(value) {
    if (!value) return '-';
    const dt = new Date(value);
    if (!Number.isFinite(dt.getTime())) return '-';
    return dt.toLocaleString();
}

function prettyStatus(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return 'Submitted';
    if (raw === 'delivered') return 'Completed';
    return raw.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function statusChipColor(status) {
    switch (String(status || '').toLowerCase()) {
        case 'delivered':
        case 'generated':
            return 'success';
        case 'in_review':
        case 'generating':
            return 'info';
        case 'on_hold':
            return 'warning';
        case 'failed':
        case 'cancelled':
            return 'error';
        default:
            return 'default';
    }
}

export function getRiskRagTone(value) {
    switch (String(value || '').trim().toLowerCase()) {
        case 'red': return 'error';
        case 'amber': return 'warning';
        case 'green': return 'success';
        default: return 'default';
    }
}

export function canQueueFeasibilityGenerationStatus(value) {
    return ['in_review', 'failed'].includes(String(value || '').trim().toLowerCase());
}

export function shouldPollFeasibilityGeneration(value) {
    return String(value || '').trim().toLowerCase() === 'generating';
}

export function generationFailureMessage(error) {
    const message = String(error?.message || '').trim();
    if (/s3:getobject/i.test(message) && /not authorized/i.test(message)) {
        return 'The report template could not be read from S3. Grant the backend worker s3:GetObject permission for the configured Feasibility template, then retry.';
    }
    return message || 'The previous generation failed. Review the worker configuration, then retry to create a new report revision.';
}

function formatBytes(value) {
    const bytes = Number(value) || 0;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatCalculatedMetric(metric) {
    if (!metric || metric.status !== 'available') return 'Unavailable';
    const number = Number(metric.value);
    if (!Number.isFinite(number)) return displayValue(metric.value);
    if (metric.unit === 'percentage') return `${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    if (metric.unit === 'square_metres') return `${number.toLocaleString(undefined, { maximumFractionDigits: 2 })} sq m`;
    if (metric.unit === 'square_feet') return `${number.toLocaleString(undefined, { maximumFractionDigits: 2 })} sq ft`;
    if (metric.unit === 'money_per_square_foot') return `£${number.toLocaleString(undefined, { maximumFractionDigits: 2 })} / sq ft`;
    return `£${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function metricLabel(value) {
    return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function displayValue(value) {
    if (value === null || value === undefined || value === '') return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.map(displayValue).join(', ') : 'Not provided';
    if (typeof value === 'object') return value.file_name || value.original_file_name || (value.document_id ? `Document ${value.document_id}` : 'Not provided');
    return String(value);
}

export function resolveAnswerValue(answers, fieldPath, rowIndex) {
    const segments = String(fieldPath || '').split('.').filter(Boolean);
    let value = answers;
    let repeatingRowResolved = false;

    for (const segment of segments) {
        if (Array.isArray(value)) {
            if (!Number.isInteger(rowIndex)) return undefined;
            value = value[rowIndex];
            repeatingRowResolved = true;
        }
        value = value && typeof value === 'object' ? value[segment] : undefined;
    }

    if (Array.isArray(value) && Number.isInteger(rowIndex) && !repeatingRowResolved) {
        return value[rowIndex];
    }
    return value;
}

function collectCommentFields(fields, output = [], parentPath = '', parentLabel = '') {
    for (const field of fields || []) {
        if (!field?.id || !field?.label) continue;
        const fieldPath = parentPath ? `${parentPath}.${field.id}` : field.id;
        const fieldLabel = parentLabel ? `${parentLabel}: ${field.label}` : field.label;
        output.push({ id: fieldPath, label: fieldLabel });
        if (Array.isArray(field?.fields)) collectCommentFields(field.fields, output, fieldPath, fieldLabel);
    }
    return output;
}

function getCommentSections(manifest) {
    return (manifest?.pages || []).flatMap((page) => (page.sections || []).map((section) => ({
        id: section.id,
        label: `${page.title}: ${section.title}`,
        fields: collectCommentFields(section.fields)
    })));
}

function reviewTargetKey(sectionKey, fieldKey, rowIndex) {
    return `${sectionKey}:${fieldKey}:${Number.isInteger(rowIndex) ? rowIndex : ''}`;
}

export function canonicalReviewPath(fieldPath, rowIndex) {
    if (!Number.isInteger(rowIndex)) return fieldPath;
    const [root, ...rest] = String(fieldPath).split('.');
    return `${root}[${rowIndex}]${rest.length ? `.${rest.join('.')}` : ''}`;
}

function FieldReviewControl({ fieldPath, rowIndex, sourceValue, review, locked, saving, onSave }) {
    const [status, setStatus] = useState(review?.review_status || 'unreviewed');
    const [overrideValue, setOverrideValue] = useState(review?.override_value ?? '');
    const [comment, setComment] = useState(review?.review_comment || '');

    useEffect(() => {
        setStatus(review?.review_status || 'unreviewed');
        setOverrideValue(review?.override_value ?? '');
        setComment(review?.review_comment || '');
    }, [review]);

    const canonicalPath = canonicalReviewPath(fieldPath, rowIndex);
    const effectiveValue = review?.effective_value ?? sourceValue;
    const amendmentInvalid = status === 'amended' && (overrideValue === '' || !comment.trim());
    return (
        <Box sx={{ gridColumn: '1 / -1', mt: 0.5, p: 1, borderLeft: '3px solid', borderColor: status === 'amended' ? 'warning.main' : 'info.light', bgcolor: 'action.hover' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Chip size="small" variant="outlined" color="info" label="Client questionnaire" />
                <Typography variant="caption" color="text.secondary">Original: {displayValue(review?.source_value ?? sourceValue)}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 800 }}>Effective: {displayValue(effectiveValue)}</Typography>
                <TextField
                    select
                    size="small"
                    label="Review state"
                    value={status}
                    disabled={locked || saving}
                    onChange={(event) => setStatus(event.target.value)}
                    sx={{ minWidth: 180, ml: { md: 'auto !important' } }}
                >
                    <MenuItem value="unreviewed">Unreviewed</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="amended">Amended</MenuItem>
                    <MenuItem value="insufficient_evidence">Insufficient evidence</MenuItem>
                    <MenuItem value="not_applicable">Not applicable</MenuItem>
                </TextField>
            </Stack>
            {status === 'amended' ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 0.45fr) minmax(280px, 1fr)' }, gap: 1, mt: 1 }}>
                    <TextField size="small" label="Surveyor value" value={overrideValue} disabled={locked || saving} onChange={(event) => setOverrideValue(event.target.value)} />
                    <TextField size="small" label="Amendment reason" value={comment} disabled={locked || saving} onChange={(event) => setComment(event.target.value)} />
                </Box>
            ) : null}
            {!locked ? (
                <Button
                    size="small"
                    variant="outlined"
                    disabled={saving || amendmentInvalid}
                    onClick={() => onSave({ field_path: canonicalPath, review_status: status, override_value: overrideValue, review_comment: comment })}
                    sx={{ mt: 1 }}
                >
                    {saving ? 'Saving...' : 'Save Review'}
                </Button>
            ) : null}
        </Box>
    );
}

function emptyRiskFields() {
    const fields = { OVERALL_RISK_POSITION: '' };
    for (let index = 1; index <= 8; index += 1) {
        const suffix = String(index).padStart(2, '0');
        fields[`RISK_${suffix}_HEADING`] = '';
        fields[`RISK_${suffix}_DESCRIPTION`] = '';
        fields[`RISK_${suffix}_RAG`] = '';
    }
    return fields;
}

function normalizeRiskDraft(fields) {
    const source = fields && typeof fields === 'object' ? fields : {};
    return Object.fromEntries(Object.keys(emptyRiskFields()).map((key) => [key, String(source[key] || '')]));
}

function AnswerField({ field, value, sectionKey, fieldPath = '', rowIndex = null, reviewTargetKeys, fieldReviewsByPath, savingTargetKey, savingReviewPath, reviewLocked, onReviewTargetChange, onFieldReviewSave }) {
    const currentPath = fieldPath || field.id;
    if (field.type === 'group') {
        const groupValue = value && typeof value === 'object' ? value : {};
        return (
            <Box sx={{ py: 1.2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>{field.label}</Typography>
                <Box sx={{ pl: 1.5, borderLeft: '3px solid', borderColor: 'divider' }}>
                    {(field.fields || []).map((child) => (
                        <AnswerField
                            key={child.id}
                            field={child}
                            value={groupValue[child.id]}
                            sectionKey={sectionKey}
                            fieldPath={`${currentPath}.${child.id}`}
                            rowIndex={rowIndex}
                            reviewTargetKeys={reviewTargetKeys}
                            fieldReviewsByPath={fieldReviewsByPath}
                            savingTargetKey={savingTargetKey}
                            savingReviewPath={savingReviewPath}
                            reviewLocked={reviewLocked}
                            onReviewTargetChange={onReviewTargetChange}
                            onFieldReviewSave={onFieldReviewSave}
                        />
                    ))}
                </Box>
            </Box>
        );
    }
    if (field.type === 'repeating_group') {
        const rows = Array.isArray(value) ? value : [];
        return (
            <Box sx={{ py: 1.2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{field.label}</Typography>
                {rows.length ? rows.map((row, index) => (
                    <Box key={`${field.id}-${index}`} sx={{ mt: 1, pl: 1.5, borderLeft: '3px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">Entry {index + 1}</Typography>
                        {(field.fields || []).map((child) => (
                            <AnswerField
                                key={child.id}
                                field={child}
                                value={row?.[child.id]}
                                sectionKey={sectionKey}
                                fieldPath={`${currentPath}.${child.id}`}
                                rowIndex={index}
                                reviewTargetKeys={reviewTargetKeys}
                                fieldReviewsByPath={fieldReviewsByPath}
                                savingTargetKey={savingTargetKey}
                                savingReviewPath={savingReviewPath}
                                reviewLocked={reviewLocked}
                                onReviewTargetChange={onReviewTargetChange}
                                onFieldReviewSave={onFieldReviewSave}
                            />
                        ))}
                    </Box>
                )) : <Typography variant="body2" color="text.secondary">Not provided</Typography>}
            </Box>
        );
    }
    const targetKey = reviewTargetKey(sectionKey, currentPath, rowIndex);
    const canonicalPath = canonicalReviewPath(currentPath, rowIndex);
    const flagged = reviewTargetKeys.has(targetKey);
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(180px, 0.7fr) minmax(220px, 1fr) 120px' }, gap: 1, alignItems: 'center', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">{field.label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{displayValue(value)}</Typography>
            <TextField
                select
                size="small"
                value={flagged ? 'yes' : 'na'}
                disabled={reviewLocked || savingTargetKey === targetKey}
                onChange={(event) => onReviewTargetChange({ sectionKey, fieldKey: currentPath, rowIndex, flagged: event.target.value === 'yes' })}
                inputProps={{ 'aria-label': `Commentary required for ${field.label}` }}
            >
                <MenuItem value="na">N/A</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
            </TextField>
            <FieldReviewControl
                key={`${canonicalPath}:${fieldReviewsByPath.get(canonicalPath)?.updated_at || 'new'}`}
                fieldPath={currentPath}
                rowIndex={rowIndex}
                sourceValue={value}
                review={fieldReviewsByPath.get(canonicalPath)}
                locked={reviewLocked}
                saving={savingReviewPath === canonicalPath}
                onSave={onFieldReviewSave}
            />
        </Box>
    );
}

export default function FeasibilityHub({ authUser, organisationName, onGoToReports }) {
    const theme = useTheme();
    const fullScreenWorkbench = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deals, setDeals] = useState([]);
    const [startingReviewId, setStartingReviewId] = useState(null);
    const [workbench, setWorkbench] = useState(null);
    const [workbenchLoading, setWorkbenchLoading] = useState(false);
    const [workbenchTab, setWorkbenchTab] = useState(0);
    const [queueingGeneration, setQueueingGeneration] = useState(false);
    const [reportAction, setReportAction] = useState('');
    const [collaboraSession, setCollaboraSession] = useState(null);
    const collaboraFrameRef = useRef(null);
    const collaboraFormRef = useRef(null);
    const collaboraFrameNameRef = useRef(`monsurv-feasibility-collabora-${Math.random().toString(36).slice(2)}`);
    const collaboraRefreshRequestRef = useRef(null);
    const [issueDialogOpen, setIssueDialogOpen] = useState(false);
    const [issueDocumentName, setIssueDocumentName] = useState('');
    const [issueError, setIssueError] = useState('');
    const [issuingReport, setIssuingReport] = useState(false);
    const [commentSectionKey, setCommentSectionKey] = useState('');
    const [commentFieldKey, setCommentFieldKey] = useState('');
    const [commentText, setCommentText] = useState('');
    const [savingComment, setSavingComment] = useState(false);
    const [savingTargetKey, setSavingTargetKey] = useState('');
    const [savingReviewPath, setSavingReviewPath] = useState('');
    const [targetCommentDrafts, setTargetCommentDrafts] = useState({});
    const [savingTargetCommentKey, setSavingTargetCommentKey] = useState('');
    const [riskDraft, setRiskDraft] = useState(emptyRiskFields);
    const [inferringRisks, setInferringRisks] = useState(false);
    const [savingRisks, setSavingRisks] = useState(false);
    const deepLinkHandled = useRef(false);
    const riskInferenceDealRef = useRef(null);
    const openWorkbenchRef = useRef(null);

    useEffect(() => {
        if (!collaboraSession?.launch_form?.action) return undefined;
        const expectedOrigin = new URL(collaboraSession.launch_form.action).origin;
        const postToEditor = (message) => collaboraFrameRef.current?.contentWindow?.postMessage(JSON.stringify(message), expectedOrigin);
        const onMessage = (event) => {
            if (event.source !== collaboraFrameRef.current?.contentWindow || event.origin !== expectedOrigin) return;
            let payload = event.data;
            if (typeof payload === 'string') {
                try { payload = JSON.parse(payload); } catch { return; }
            }
            const messageId = String(payload?.MessageId || '').toLowerCase();
            if (messageId !== 'app_tokenexpired' && messageId !== 'app_tokenexpiring') return;
            if (collaboraRefreshRequestRef.current) return;
            const refresh = refreshClientdealFeasibilityCollaboraToken(collaboraSession.deal_id, collaboraSession.artifact_id)
                .then((json) => {
                    const token = String(json?.access_token || '');
                    const ttl = Number(json?.access_token_ttl || 0);
                    if (!token || !Number.isFinite(ttl) || ttl <= Date.now()) throw new Error('The refreshed Collabora token is invalid.');
                    postToEditor({ MessageId: 'Reset_Access_Token', Values: { token, ttl: String(ttl) } });
                });
            collaboraRefreshRequestRef.current = refresh;
            refresh.catch((err) => setError(err?.message || 'Failed to refresh the Collabora session.')).finally(() => {
                if (collaboraRefreshRequestRef.current === refresh) collaboraRefreshRequestRef.current = null;
            });
        };
        window.addEventListener('message', onMessage);
        collaboraFormRef.current?.submit();
        return () => window.removeEventListener('message', onMessage);
    }, [collaboraSession]);

    useEffect(() => {
        let cancelled = false;

        async function loadDeals() {
            setLoading(true);
            setError('');
            try {
                const payload = await listClientdealDeals();
                if (!cancelled) {
                    setDeals(Array.isArray(payload?.items) ? payload.items : []);
                }
            } catch (err) {
                if (!cancelled) setError(err?.message || 'Failed to load feasibility deals.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadDeals();
        return () => { cancelled = true; };
    }, []);

    async function startReview(deal) {
        setStartingReviewId(deal.id);
        setError('');
        try {
            const payload = await updateClientdealDealStatus(deal.id, 'in_review', 'Review started from the Feasibility Deal Intake hub.');
            const updated = payload?.item;
            setDeals((current) => current.map((item) => item.id === deal.id ? { ...item, ...updated } : item));
            await openWorkbench(updated || deal);
        } catch (err) {
            setError(err?.message || 'Failed to start the Feasibility review.');
        } finally {
            setStartingReviewId(null);
        }
    }

    async function openWorkbench(deal) {
        setWorkbenchLoading(true);
        setWorkbenchTab(0);
        setCommentFieldKey('');
        setCommentText('');
        setTargetCommentDrafts({});
        setRiskDraft(emptyRiskFields());
        riskInferenceDealRef.current = null;
        setError('');
        try {
            const payload = await getClientdealDeal(deal.id);
            setWorkbench(payload);
            setRiskDraft(normalizeRiskDraft(payload?.risk_assessment?.final_fields));
            setCommentSectionKey(getCommentSections(payload?.manifest)[0]?.id || 'general');
            if (String(payload?.item?.status || '').toLowerCase() === 'in_review' && !payload?.risk_assessment) {
                void inferRisksOnce(deal.id);
            }
        } catch (err) {
            setError(err?.message || 'Failed to open the Feasibility workbench.');
        } finally {
            setWorkbenchLoading(false);
        }
    }

    async function inferRisksOnce(dealId) {
        const normalizedDealId = Number(dealId);
        if (!normalizedDealId || riskInferenceDealRef.current === normalizedDealId) return;
        riskInferenceDealRef.current = normalizedDealId;
        setInferringRisks(true);
        setError('');
        try {
            await inferClientdealFeasibilityRisks(normalizedDealId);
            const refreshed = await getClientdealDeal(normalizedDealId);
            setWorkbench(refreshed);
            setRiskDraft(normalizeRiskDraft(refreshed?.risk_assessment?.final_fields));
        } catch (err) {
            setError(err?.message || 'Failed to infer Feasibility risks.');
            try {
                const refreshed = await getClientdealDeal(normalizedDealId);
                setWorkbench(refreshed);
            } catch { }
        } finally {
            setInferringRisks(false);
        }
    }

    openWorkbenchRef.current = openWorkbench;

    useEffect(() => {
        const dealId = Number(workbench?.item?.id);
        if (!dealId || workbench?.risk_assessment?.status !== 'generating') return undefined;
        const timer = setInterval(() => {
            getClientdealDeal(dealId).then((refreshed) => {
                setWorkbench(refreshed);
                if (refreshed?.risk_assessment?.status === 'completed') {
                    setRiskDraft(normalizeRiskDraft(refreshed.risk_assessment.final_fields));
                }
            }).catch(() => { });
        }, 3000);
        return () => clearInterval(timer);
    }, [workbench?.item?.id, workbench?.risk_assessment?.status]);

    useEffect(() => {
        const dealId = Number(workbench?.item?.id);
        if (!dealId || !shouldPollFeasibilityGeneration(workbench?.item?.status)) return undefined;
        const timer = setInterval(() => {
            getClientdealDeal(dealId).then((refreshed) => {
                setWorkbench(refreshed);
                setDeals((current) => current.map((item) => Number(item.id) === dealId
                    ? { ...item, ...refreshed?.item }
                    : item));
            }).catch(() => { });
        }, 3000);
        return () => clearInterval(timer);
    }, [workbench?.item?.id, workbench?.item?.status]);

    useEffect(() => {
        if (loading || deepLinkHandled.current) return;
        const dealId = Number(new URLSearchParams(window.location.search).get('deal'));
        if (!Number.isInteger(dealId) || dealId <= 0) return;
        deepLinkHandled.current = true;
        const deal = deals.find((item) => Number(item.id) === dealId);
        if (deal) openWorkbenchRef.current?.(deal);
        else setError('The linked Feasibility deal is unavailable or you do not have access to it.');
    }, [deals, loading]);

    async function queueGeneration() {
        const dealId = Number(workbench?.item?.id);
        if (!dealId) return;
        setQueueingGeneration(true);
        setError('');
        try {
            const payload = await queueClientdealFeasibilityGeneration(dealId);
            const refreshed = await getClientdealDeal(dealId);
            setWorkbench(refreshed);
            setDeals((current) => current.map((item) => Number(item.id) === dealId
                ? { ...item, status: 'generating', updated_at: new Date().toISOString() }
                : item));
            setWorkbenchTab(5);
            return payload;
        } catch (err) {
            setError(err?.message || 'Failed to queue Feasibility report generation.');
        } finally {
            setQueueingGeneration(false);
        }
    }

    function updateRiskField(key, value) {
        setRiskDraft((current) => ({ ...current, [key]: value }));
    }

    function clearRiskRow(index) {
        const suffix = String(index).padStart(2, '0');
        setRiskDraft((current) => ({
            ...current,
            [`RISK_${suffix}_HEADING`]: '',
            [`RISK_${suffix}_DESCRIPTION`]: '',
            [`RISK_${suffix}_RAG`]: ''
        }));
    }

    async function saveRisks() {
        const dealId = Number(workbench?.item?.id);
        if (!dealId) return;
        setSavingRisks(true);
        setError('');
        try {
            const payload = await updateClientdealFeasibilityRisks(dealId, riskDraft);
            setWorkbench((current) => ({ ...current, risk_assessment: payload.risk_assessment }));
            setRiskDraft(normalizeRiskDraft(payload?.risk_assessment?.final_fields));
        } catch (err) {
            setError(err?.message || 'Failed to save Feasibility risks.');
        } finally {
            setSavingRisks(false);
        }
    }

    async function refreshWorkbench(dealId) {
        const refreshed = await getClientdealDeal(dealId);
        setWorkbench(refreshed);
        setDeals((current) => current.map((item) => Number(item.id) === Number(dealId)
            ? { ...item, ...refreshed?.item }
            : item));
        return refreshed;
    }

    async function openReportDownload(loader, actionName) {
        const dealId = Number(workbench?.item?.id);
        if (!dealId) return;
        const popup = window.open('about:blank', '_blank');
        setReportAction(actionName);
        setError('');
        try {
            const payload = await loader(dealId);
            if (!payload?.download_url) throw new Error('The report download is unavailable.');
            if (popup) popup.location.replace(payload.download_url);
            else window.open(payload.download_url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            if (popup) popup.close();
            setError(err?.message || 'Failed to open the Feasibility report.');
        } finally {
            setReportAction('');
        }
    }

    async function openFeasibilityCollabora(startQa = false) {
        const dealId = Number(workbench?.item?.id);
        if (!dealId) return;
        setReportAction('collabora');
        setError('');
        try {
            if (startQa) {
                await updateClientdealDealStatus(dealId, 'qa_review', 'Generated working report moved into online surveyor QA review.');
            }
            const payload = await launchClientdealFeasibilityCollabora(dealId);
            if (!payload?.launch_form?.action || !payload?.launch_form?.fields?.access_token || Number(payload?.launch_form?.fields?.access_token_ttl || 0) <= Date.now()) {
                throw new Error('A valid Collabora launch form was not returned.');
            }
            await refreshWorkbench(dealId);
            setCollaboraSession(payload);
            setIssueDocumentName(payload.suggested_document_name || `${workbench?.item?.deal_reference || ''} - ${workbench?.item?.deal_name || ''} Feasibility Report`);
        } catch (err) {
            setError(err?.message || 'Failed to open the Feasibility report in Collabora.');
            if (startQa) {
                try { await refreshWorkbench(dealId); } catch { }
            }
        } finally {
            setReportAction('');
        }
    }

    async function issueReport() {
        const dealId = Number(workbench?.item?.id);
        const documentName = issueDocumentName.trim();
        if (!dealId || !documentName) return;
        setIssuingReport(true);
        setIssueError('');
        try {
            const payload = await issueClientdealFeasibilityReport(dealId, documentName);
            const refreshed = await getClientdealDeal(dealId);
            setWorkbench(refreshed);
            setDeals((current) => current.map((item) => Number(item.id) === dealId ? { ...item, ...payload.item } : item));
            setIssueDialogOpen(false);
            setCollaboraSession(null);
            setWorkbenchTab(5);
        } catch (err) {
            setIssueError(err?.message || 'Failed to issue the Feasibility report.');
        } finally {
            setIssuingReport(false);
        }
    }

    const workbenchAnswers = useMemo(
        () => workbench?.questionnaire?.answers || {},
        [workbench?.questionnaire?.answers]
    );
    const commentSections = useMemo(() => getCommentSections(workbench?.manifest), [workbench?.manifest]);
    const selectedCommentSection = commentSections.find((section) => section.id === commentSectionKey) || null;
    const reviewTargetKeys = useMemo(() => new Set((workbench?.review_targets || []).map((target) => (
        reviewTargetKey(target.section_key, target.field_key, target.row_index)
    ))), [workbench?.review_targets]);
    const fieldReviewsByPath = useMemo(() => new Map((workbench?.field_reviews || []).map((review) => [review.field_path, review])), [workbench?.field_reviews]);
    const flaggedCommentTargets = useMemo(() => (workbench?.review_targets || []).map((target) => {
        const section = commentSections.find((item) => item.id === target.section_key);
        const field = section?.fields?.find((item) => item.id === target.field_key);
        const answer = resolveAnswerValue(workbenchAnswers, target.field_key, target.row_index);
        return {
            ...target,
            target_key: reviewTargetKey(target.section_key, target.field_key, target.row_index),
            section_label: section?.label || target.section_key,
            field_label: field?.label || target.field_key,
            entry_label: Number.isInteger(target.row_index) ? `Entry ${target.row_index + 1}` : '',
            answer_label: displayValue(answer)
        };
    }), [commentSections, workbench?.review_targets, workbenchAnswers]);
    const reviewLocked = String(workbench?.item?.status || '').toLowerCase() !== 'in_review';
    const reportStatus = String(workbench?.item?.status || '').toLowerCase();
    const canQueueReport = canQueueFeasibilityGenerationStatus(reportStatus);
    const latestFailedRun = (workbench?.generation_runs || []).find((run) => run.status === 'failed');
    const riskAssessment = workbench?.risk_assessment || null;
    const riskAssessmentComplete = riskAssessment?.status === 'completed';
    const riskAiFields = normalizeRiskDraft(riskAssessment?.ai_fields);
    const riskDraftValid = Boolean(String(riskDraft.OVERALL_RISK_POSITION || '').trim()) && Array.from({ length: 8 }, (_, index) => {
        const suffix = String(index + 1).padStart(2, '0');
        const values = [riskDraft[`RISK_${suffix}_HEADING`], riskDraft[`RISK_${suffix}_DESCRIPTION`], riskDraft[`RISK_${suffix}_RAG`]].map((value) => String(value || '').trim());
        return values.filter(Boolean).length === 0 || (values.every(Boolean) && ['Green', 'Amber', 'Red'].includes(values[2]));
    }).every(Boolean);
    const riskDraftChanged = riskAssessmentComplete
        && JSON.stringify(normalizeRiskDraft(riskAssessment?.final_fields)) !== JSON.stringify(normalizeRiskDraft(riskDraft));

    async function updateReviewTarget({ sectionKey, fieldKey, rowIndex, flagged }) {
        const dealId = Number(workbench?.item?.id);
        if (!dealId) return;
        const targetKey = reviewTargetKey(sectionKey, fieldKey, rowIndex);
        setSavingTargetKey(targetKey);
        setError('');
        try {
            const payload = await updateClientdealFeasibilityReviewTarget(dealId, {
                section_key: sectionKey,
                field_key: fieldKey,
                row_index: Number.isInteger(rowIndex) ? rowIndex : null,
                flagged
            });
            setWorkbench((current) => {
                const retained = (current?.review_targets || []).filter((target) => (
                    reviewTargetKey(target.section_key, target.field_key, target.row_index) !== targetKey
                ));
                return { ...current, review_targets: payload?.target ? [...retained, payload.target] : retained };
            });
        } catch (err) {
            setError(err?.message || 'Failed to update the commentary flag.');
        } finally {
            setSavingTargetKey('');
        }
    }

    async function saveFieldReview(review) {
        const dealId = Number(workbench?.item?.id);
        if (!dealId) return;
        setSavingReviewPath(review.field_path);
        setError('');
        try {
            const payload = await updateClientdealFeasibilityFieldReview(dealId, review);
            setWorkbench((current) => {
                const retained = (current?.field_reviews || []).filter((item) => item.field_path !== review.field_path);
                return { ...current, field_reviews: [...retained, payload.review] };
            });
        } catch (err) {
            setError(err?.message || 'Failed to save the field review.');
        } finally {
            setSavingReviewPath('');
        }
    }

    async function saveTargetComment(target) {
        const dealId = Number(workbench?.item?.id);
        const text = String(targetCommentDrafts[target.target_key] || '').trim();
        if (!dealId || !text) return;
        setSavingTargetCommentKey(target.target_key);
        setError('');
        try {
            const payload = await createClientdealFeasibilityComment(dealId, {
                section_key: target.section_key,
                field_key: target.field_key,
                row_index: Number.isInteger(target.row_index) ? target.row_index : null,
                comment_text: text
            });
            setWorkbench((current) => ({ ...current, surveyor_comments: [payload.comment, ...(current?.surveyor_comments || [])] }));
            setTargetCommentDrafts((current) => ({ ...current, [target.target_key]: '' }));
        } catch (err) {
            setError(err?.message || 'Failed to add surveyor comment.');
        } finally {
            setSavingTargetCommentKey('');
        }
    }

    async function saveComment() {
        const dealId = Number(workbench?.item?.id);
        if (!dealId || !commentSectionKey || !commentText.trim()) return;
        setSavingComment(true);
        setError('');
        try {
            const payload = await createClientdealFeasibilityComment(dealId, {
                section_key: commentSectionKey,
                field_key: commentFieldKey || null,
                comment_text: commentText.trim()
            });
            setWorkbench((current) => ({
                ...current,
                surveyor_comments: [payload.comment, ...(current?.surveyor_comments || [])]
            }));
            setCommentText('');
        } catch (err) {
            setError(err?.message || 'Failed to add surveyor comment.');
        } finally {
            setSavingComment(false);
        }
    }

    const metrics = useMemo(() => {
        const rows = Array.isArray(deals) ? deals : [];
        return {
            total: rows.length,
            submitted: rows.filter((d) => String(d?.status || '').toLowerCase() === 'submitted').length,
            inReview: rows.filter((d) => String(d?.status || '').toLowerCase() === 'in_review').length,
            generated: rows.filter((d) => String(d?.status || '').toLowerCase() === 'generated').length,
            completed: rows.filter((d) => String(d?.status || '').toLowerCase() === 'delivered').length
        };
    }, [deals]);

    return (
        <Stack spacing={2.2}>
            <Paper
                sx={{
                    p: { xs: 1.5, sm: 2.2 },
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                    background: `linear-gradient(130deg, ${alpha('#ffffff', 0.94)} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`
                }}
            >
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={1.5}>
                    <Box>
                        <Typography variant="overline" sx={{ letterSpacing: 1, color: 'text.secondary', fontWeight: 800 }}>
                            MonTech Feasibility
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.2 }}>
                            Feasibility Deal Intake
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.4 }}>
                            Surveyor jump-off page for deals submitted through clientdeal.monsurv.com and clientdeal.monsurv.co.uk.
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button variant="outlined" onClick={() => onGoToReports && onGoToReports()}>
                            Go To Projects
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                <Chip label={`Total: ${metrics.total}`} />
                <Chip color="default" label={`Submitted: ${metrics.submitted}`} />
                <Chip color="info" label={`In Review: ${metrics.inReview}`} />
                <Chip color="success" label={`Generated: ${metrics.generated}`} />
                <Chip color="success" variant="outlined" label={`Completed: ${metrics.completed}`} />
            </Stack>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 800 }}>Deal Name</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Reference</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Client</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Submitted</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Updated</TableCell>
                            <TableCell sx={{ fontWeight: 800, minWidth: 180 }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7}>Loading feasibility deals...</TableCell>
                            </TableRow>
                        ) : null}

                        {!loading && deals.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>No feasibility deals have been submitted yet.</TableCell>
                            </TableRow>
                        ) : null}

                        {!loading && deals.map((deal) => (
                            <TableRow key={deal.id} hover>
                                <TableCell>{deal.deal_name || '-'}</TableCell>
                                <TableCell>{deal.deal_reference || '-'}</TableCell>
                                <TableCell>{deal.client_email || '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        color={statusChipColor(deal.status)}
                                        label={prettyStatus(deal.status)}
                                    />
                                </TableCell>
                                <TableCell>{formatDateTime(deal.submitted_at)}</TableCell>
                                <TableCell>{formatDateTime(deal.updated_at)}</TableCell>
                                <TableCell>
                                    {String(deal.status || '').toLowerCase() === 'submitted' ? (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={startingReviewId === deal.id}
                                            onClick={() => startReview(deal)}
                                        >
                                            {startingReviewId === deal.id ? 'Starting...' : 'Start Review'}
                                        </Button>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => openWorkbench(deal)}
                                        >
                                            Open Workspace
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Signed in as {authUser?.email || authUser?.username || 'MonTech user'}.
            </Typography>

            <Dialog fullScreen={fullScreenWorkbench} fullWidth maxWidth="lg" open={Boolean(workbench) || workbenchLoading} onClose={() => setWorkbench(null)}>
                <DialogTitle component="div" sx={{ pr: 7 }}>
                    <Typography variant="overline" color="text.secondary">Feasibility Workbench</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{workbench?.item?.deal_name || 'Loading deal...'}</Typography>
                    <IconButton aria-label="Close workbench" onClick={() => setWorkbench(null)} sx={{ position: 'absolute', right: 12, top: 12 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                {workbenchLoading ? (
                    <DialogContent sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}><CircularProgress /></DialogContent>
                ) : (
                    <>
                        <Box sx={{ px: { xs: 1, sm: 3 }, pt: 1 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                <Chip size="small" label={workbench?.item?.deal_reference || '-'} />
                                <Chip size="small" color={statusChipColor(workbench?.item?.status)} label={prettyStatus(workbench?.item?.status)} />
                                <Typography variant="body2" color="text.secondary">{workbench?.item?.client_email || '-'}</Typography>
                            </Stack>
                            <Tabs value={workbenchTab} onChange={(_, value) => setWorkbenchTab(value)} variant="scrollable" scrollButtons="auto" sx={{ mt: 1 }}>
                                <Tab label="Questionnaire" />
                                <Tab label={`Evidence (${workbench?.documents?.length || 0})`} />
                                <Tab label={`Comments (${workbench?.surveyor_comments?.length || 0})`} />
                                <Tab label="Risks" />
                                <Tab label={`Activity (${workbench?.status_events?.length || 0})`} />
                                <Tab label="Report" />
                            </Tabs>
                        </Box>
                        <DialogContent dividers sx={{ minHeight: { md: 520 } }}>
                            {workbenchTab === 0 ? (
                                <Stack spacing={2}>
                                    <Alert severity={workbench?.readiness?.ready ? 'success' : 'warning'}>
                                        {workbench?.readiness?.ready
                                            ? 'Effective facts are ready for report generation.'
                                            : `${workbench?.readiness?.material_information_gaps?.length || 0} material information gap(s); ${workbench?.readiness?.unreviewed_field_count || 0} saved field review(s) remain unreviewed.`}
                                    </Alert>
                                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5} sx={{ mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Calculated feasibility metrics</Typography>
                                            <Typography variant="caption" color="text.secondary">{workbench?.calculations?.formula_version || 'Formula unavailable'}</Typography>
                                        </Stack>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableBody>
                                                    {Object.entries(workbench?.calculations?.metrics || {}).map(([key, metric]) => (
                                                        <TableRow key={key}>
                                                            <TableCell>{metricLabel(key)}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCalculatedMetric(metric)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                    {(workbench?.manifest?.pages || []).map((page, pageIndex) => (
                                        <Accordion key={page.id} defaultExpanded={pageIndex === 0} disableGutters elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontWeight: 800 }}>{page.title}</Typography></AccordionSummary>
                                            <AccordionDetails>
                                                {(page.sections || []).map((section) => (
                                                    <Box key={section.id} sx={{ mb: 2.5 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>{section.title}</Typography>
                                                        <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: 'minmax(180px, 0.7fr) minmax(220px, 1fr) 120px', gap: 1, py: 0.75, borderBottom: '2px solid', borderColor: 'divider' }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 800 }}>Question</Typography>
                                                            <Typography variant="caption" sx={{ fontWeight: 800 }}>Response</Typography>
                                                            <Typography variant="caption" sx={{ fontWeight: 800 }}>{organisationName || 'Organisation'} Commentary</Typography>
                                                        </Box>
                                                        {(section.fields || []).map((field) => (
                                                            <AnswerField
                                                                key={field.id}
                                                                field={field}
                                                                value={workbenchAnswers[field.id]}
                                                                sectionKey={section.id}
                                                                fieldPath={field.id}
                                                                reviewTargetKeys={reviewTargetKeys}
                                                                fieldReviewsByPath={fieldReviewsByPath}
                                                                savingTargetKey={savingTargetKey}
                                                                savingReviewPath={savingReviewPath}
                                                                reviewLocked={reviewLocked}
                                                                onReviewTargetChange={updateReviewTarget}
                                                                onFieldReviewSave={saveFieldReview}
                                                            />
                                                        ))}
                                                    </Box>
                                                ))}
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            ) : null}
                            {workbenchTab === 1 ? (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead><TableRow><TableCell>File</TableCell><TableCell>Question</TableCell><TableCell>Status</TableCell><TableCell>Size</TableCell><TableCell>Uploaded</TableCell></TableRow></TableHead>
                                        <TableBody>{(workbench?.documents || []).map((document) => (
                                            <TableRow key={document.id}><TableCell>{document.original_file_name}</TableCell><TableCell>{document.question_key || '-'}</TableCell><TableCell><Chip size="small" label={prettyStatus(document.upload_status)} color={document.upload_status === 'verified' ? 'success' : 'default'} /></TableCell><TableCell>{formatBytes(document.size_bytes)}</TableCell><TableCell>{formatDateTime(document.uploaded_at || document.created_at)}</TableCell></TableRow>
                                        ))}</TableBody>
                                    </Table>
                                    {!workbench?.documents?.length ? <Typography color="text.secondary" sx={{ py: 3 }}>No evidence files were submitted.</Typography> : null}
                                </TableContainer>
                            ) : null}
                            {workbenchTab === 2 ? (
                                <Stack spacing={2.5}>
                                    {String(workbench?.item?.status || '').toLowerCase() === 'in_review' ? (
                                        <Stack spacing={1.5}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{organisationName || 'Organisation'} commentary items</Typography>
                                            {flaggedCommentTargets.map((target) => (
                                                <Paper key={target.target_key} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                                                    <Stack spacing={1.25}>
                                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                                                            <Chip size="small" label={target.section_label} />
                                                            <Chip size="small" variant="outlined" label={target.field_label} />
                                                            <Chip size="small" color="info" variant="outlined" label={`Answer: ${target.answer_label}`} />
                                                            {target.entry_label ? <Chip size="small" variant="outlined" label={target.entry_label} /> : null}
                                                        </Stack>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            minRows={3}
                                                            label="Commentary"
                                                            value={targetCommentDrafts[target.target_key] || ''}
                                                            onChange={(event) => setTargetCommentDrafts((current) => ({ ...current, [target.target_key]: event.target.value }))}
                                                            inputProps={{ maxLength: 5000 }}
                                                            helperText={`${String(targetCommentDrafts[target.target_key] || '').length}/5000`}
                                                        />
                                                        <Box>
                                                            <Button
                                                                variant="contained"
                                                                disabled={savingTargetCommentKey === target.target_key || !String(targetCommentDrafts[target.target_key] || '').trim()}
                                                                onClick={() => saveTargetComment(target)}
                                                            >
                                                                {savingTargetCommentKey === target.target_key ? 'Saving...' : 'Add Comment'}
                                                            </Button>
                                                        </Box>
                                                    </Stack>
                                                </Paper>
                                            ))}
                                            {!flaggedCommentTargets.length ? <Alert severity="info">Set a questionnaire row to Yes to add it here for commentary.</Alert> : null}
                                            <Divider />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Add review comment</Typography>
                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    label="Questionnaire section"
                                                    value={commentSectionKey}
                                                    onChange={(event) => { setCommentSectionKey(event.target.value); setCommentFieldKey(''); }}
                                                >
                                                    {commentSections.map((section) => <MenuItem key={section.id} value={section.id}>{section.label}</MenuItem>)}
                                                </TextField>
                                                <TextField
                                                    select
                                                    fullWidth
                                                    label="Field (optional)"
                                                    value={commentFieldKey}
                                                    onChange={(event) => setCommentFieldKey(event.target.value)}
                                                >
                                                    <MenuItem value="">Entire section</MenuItem>
                                                    {(selectedCommentSection?.fields || []).map((field) => <MenuItem key={field.id} value={field.id}>{field.label}</MenuItem>)}
                                                </TextField>
                                            </Stack>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                label="Surveyor comment"
                                                value={commentText}
                                                onChange={(event) => setCommentText(event.target.value)}
                                                inputProps={{ maxLength: 5000 }}
                                                helperText={`${commentText.length}/5000`}
                                            />
                                            <Box><Button variant="contained" disabled={savingComment || !commentText.trim()} onClick={saveComment}>{savingComment ? 'Saving...' : 'Add Comment'}</Button></Box>
                                        </Stack>
                                    ) : <Alert severity="info">Comments are locked once report generation begins.</Alert>}
                                    <Divider />
                                    <Stack spacing={0} divider={<Divider flexItem />}>
                                        {(workbench?.surveyor_comments || []).map((comment) => (
                                            <Box key={comment.id} sx={{ py: 1.5 }}>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                                    <Chip size="small" label={comment.section_key} />
                                                    {comment.field_key ? <Chip size="small" variant="outlined" label={comment.field_key} /> : null}
                                                    {Number.isInteger(comment.row_index) ? <Chip size="small" variant="outlined" label={`Entry ${comment.row_index + 1}`} /> : null}
                                                    <Typography variant="caption" color="text.secondary">Revision {comment.revision} · {formatDateTime(comment.created_at)}</Typography>
                                                </Stack>
                                                <Typography variant="body2" sx={{ mt: 0.8, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{comment.comment_text}</Typography>
                                                <Typography variant="caption" color="text.secondary">{comment.author_email}</Typography>
                                            </Box>
                                        ))}
                                        {!workbench?.surveyor_comments?.length ? <Typography color="text.secondary" sx={{ py: 3 }}>No surveyor comments recorded.</Typography> : null}
                                    </Stack>
                                </Stack>
                            ) : null}
                            {workbenchTab === 3 ? (
                                <Stack spacing={2}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Feasibility risks</Typography>
                                            {riskAssessment?.inferred_at ? <Typography variant="caption" color="text.secondary">Inferred {formatDateTime(riskAssessment.inferred_at)}</Typography> : null}
                                        </Box>
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {riskAssessment?.model_name ? <Chip size="small" variant="outlined" label={riskAssessment.model_name} /> : null}
                                            {riskAssessmentComplete ? <Chip size="small" color="success" label={`Review revision ${riskAssessment.revision || 0}`} /> : null}
                                        </Stack>
                                    </Stack>
                                    {inferringRisks || riskAssessment?.status === 'generating' ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
                                            <CircularProgress size={22} />
                                            <Typography variant="body2">Preparing risk assessment...</Typography>
                                        </Box>
                                    ) : null}
                                    {riskAssessment?.status === 'failed' ? <Alert severity="error">{riskAssessment.error || 'Risk inference failed.'}</Alert> : null}
                                    {riskAssessmentComplete ? (
                                        <Stack spacing={0} divider={<Divider flexItem />}>
                                            {Array.from({ length: 8 }, (_, index) => {
                                                const slot = index + 1;
                                                const suffix = String(slot).padStart(2, '0');
                                                const headingKey = `RISK_${suffix}_HEADING`;
                                                const descriptionKey = `RISK_${suffix}_DESCRIPTION`;
                                                const ragKey = `RISK_${suffix}_RAG`;
                                                const adjusted = [headingKey, descriptionKey, ragKey].some((key) => riskDraft[key] !== riskAiFields[key]);
                                                const ragTone = getRiskRagTone(riskDraft[ragKey]);
                                                const ragColor = ragTone === 'default' ? theme.palette.divider : theme.palette[ragTone].main;
                                                const riskFieldSx = {
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '6px',
                                                        backgroundColor: theme.palette.background.paper
                                                    }
                                                };
                                                return (
                                                    <Box
                                                        key={suffix}
                                                        sx={{
                                                            my: 0.75,
                                                            p: 1.5,
                                                            border: '1px solid',
                                                            borderColor: ragColor,
                                                            borderLeftWidth: ragTone === 'default' ? 1 : 5,
                                                            borderRadius: 1,
                                                            backgroundColor: ragTone === 'default' ? 'transparent' : alpha(ragColor, 0.1),
                                                            transition: 'background-color 160ms ease, border-color 160ms ease'
                                                        }}
                                                    >
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Risk {slot}</Typography>
                                                                <Chip size="small" variant={adjusted ? 'filled' : 'outlined'} color={adjusted ? 'info' : 'default'} label={adjusted ? 'Adjusted' : 'AI initial'} />
                                                            </Stack>
                                                            {!reviewLocked ? <Button size="small" onClick={() => clearRiskRow(slot)}>Clear</Button> : null}
                                                        </Stack>
                                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 0.55fr) minmax(280px, 1fr) 130px' }, gap: 1.25 }}>
                                                            <TextField
                                                                size="small"
                                                                label="Risk heading"
                                                                value={riskDraft[headingKey]}
                                                                disabled={reviewLocked}
                                                                inputProps={{ maxLength: 500 }}
                                                                onChange={(event) => updateRiskField(headingKey, event.target.value)}
                                                                sx={riskFieldSx}
                                                            />
                                                            <TextField
                                                                size="small"
                                                                multiline
                                                                minRows={2}
                                                                label="Risk description"
                                                                value={riskDraft[descriptionKey]}
                                                                disabled={reviewLocked}
                                                                inputProps={{ maxLength: 6000 }}
                                                                onChange={(event) => updateRiskField(descriptionKey, event.target.value)}
                                                                sx={riskFieldSx}
                                                            />
                                                            <TextField
                                                                select
                                                                size="small"
                                                                label="RAG"
                                                                value={riskDraft[ragKey]}
                                                                disabled={reviewLocked}
                                                                onChange={(event) => updateRiskField(ragKey, event.target.value)}
                                                                sx={{
                                                                    ...riskFieldSx,
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: ragTone === 'default' ? undefined : ragColor,
                                                                        borderWidth: ragTone === 'default' ? undefined : 2
                                                                    },
                                                                    '& .MuiSelect-select': {
                                                                        color: ragTone === 'default' ? 'text.primary' : ragColor,
                                                                        fontWeight: ragTone === 'default' ? 400 : 800
                                                                    }
                                                                }}
                                                            >
                                                                <MenuItem value="">Not used</MenuItem>
                                                                <MenuItem value="Green">Green</MenuItem>
                                                                <MenuItem value="Amber">Amber</MenuItem>
                                                                <MenuItem value="Red">Red</MenuItem>
                                                            </TextField>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    ) : null}
                                    {riskAssessmentComplete ? (
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            label="Overall risk position"
                                            value={riskDraft.OVERALL_RISK_POSITION}
                                            disabled={reviewLocked}
                                            inputProps={{ maxLength: 6000 }}
                                            onChange={(event) => updateRiskField('OVERALL_RISK_POSITION', event.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                        />
                                    ) : null}
                                    {riskAssessmentComplete && !reviewLocked ? (
                                        <Box>
                                            <Button variant="contained" disabled={savingRisks || !riskDraftValid || !riskDraftChanged} onClick={saveRisks}>
                                                {savingRisks ? 'Saving...' : 'Save Risk Review'}
                                            </Button>
                                        </Box>
                                    ) : null}
                                </Stack>
                            ) : null}
                            {workbenchTab === 4 ? (
                                <Stack spacing={0} divider={<Divider flexItem />}>
                                    {(workbench?.status_events || []).map((event) => (
                                        <Box key={event.id} sx={{ py: 1.5 }}><Stack direction="row" spacing={1} alignItems="center"><Chip size="small" label={prettyStatus(event.next_status)} color={statusChipColor(event.next_status)} /><Typography variant="body2" color="text.secondary">{formatDateTime(event.created_at)}</Typography></Stack><Typography variant="body2" sx={{ mt: 0.7 }}>{event.event_note || 'Status updated'}</Typography><Typography variant="caption" color="text.secondary">{event.actor_email || 'System'}</Typography></Box>
                                    ))}
                                    {!workbench?.status_events?.length ? <Typography color="text.secondary" sx={{ py: 3 }}>No activity recorded.</Typography> : null}
                                </Stack>
                            ) : null}
                            {workbenchTab === 5 ? (
                                <Stack spacing={2.5}>
                                    {reportStatus === 'failed' ? (
                                        <Alert severity="error">
                                            {generationFailureMessage(latestFailedRun?.error)}
                                        </Alert>
                                    ) : null}
                                    <Box>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Report generation</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {workbench?.generation_status?.status_message || 'No generation run has been requested.'}
                                                </Typography>
                                            </Box>
                                            {workbench?.generation_status ? (
                                                <Chip
                                                    size="small"
                                                    color={statusChipColor(workbench.generation_status.stage)}
                                                    label={prettyStatus(workbench.generation_status.stage)}
                                                />
                                            ) : null}
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.max(0, Math.min(100, Number(workbench?.generation_status?.progress_percent) || 0))}
                                            sx={{ height: 8, borderRadius: 1 }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            {Number(workbench?.generation_status?.progress_percent) || 0}% complete
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>QA and delivery</Typography>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                                            {String(workbench?.item?.status || '').toLowerCase() === 'generated' ? (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<EditDocumentIcon />}
                                                    disabled={Boolean(reportAction)}
                                                    onClick={() => openFeasibilityCollabora(true)}
                                                >
                                                    {reportAction === 'collabora' ? 'Opening Collabora...' : 'Start QA Review'}
                                                </Button>
                                            ) : null}
                                            {String(workbench?.item?.status || '').toLowerCase() === 'qa_review' ? (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<EditDocumentIcon />}
                                                    disabled={Boolean(reportAction) || !workbench?.report_artifacts?.working_docx}
                                                    onClick={() => openFeasibilityCollabora(false)}
                                                >
                                                    {reportAction === 'collabora' ? 'Opening Collabora...' : 'Open in Collabora'}
                                                </Button>
                                            ) : null}
                                            {workbench?.report_artifacts?.issued_pdf ? (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<DownloadIcon />}
                                                    disabled={Boolean(reportAction)}
                                                    onClick={() => openReportDownload(getClientdealFeasibilityIssuedReportDownload, 'issued-download')}
                                                >
                                                    Download Issued PDF
                                                </Button>
                                            ) : null}
                                        </Stack>
                                        {workbench?.report_artifacts?.issued_pdf ? (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                Issued PDF version {workbench.report_artifacts.issued_pdf.version} · {formatBytes(workbench.report_artifacts.issued_pdf.size_bytes)} · {formatDateTime(workbench.report_artifacts.issued_pdf.created_at)}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                    <Divider />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Revision history</Typography>
                                        {(workbench?.generation_runs || []).length ? (
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead><TableRow><TableCell>Revision</TableCell><TableCell>Status</TableCell><TableCell>Requested</TableCell><TableCell>Completed</TableCell></TableRow></TableHead>
                                                    <TableBody>{workbench.generation_runs.map((run) => (
                                                        <TableRow key={run.id}>
                                                            <TableCell>Version {run.revision}</TableCell>
                                                            <TableCell><Chip size="small" label={prettyStatus(run.status)} color={statusChipColor(run.status)} /></TableCell>
                                                            <TableCell>{formatDateTime(run.requested_at)}</TableCell>
                                                            <TableCell>{formatDateTime(run.completed_at)}</TableCell>
                                                        </TableRow>
                                                    ))}</TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : <Typography variant="body2" color="text.secondary">No report revisions yet.</Typography>}
                                    </Box>
                                </Stack>
                            ) : null}
                        </DialogContent>
                    </>
                )}
                <DialogActions>
                    {canQueueReport ? (
                        <Button variant="contained" disabled={queueingGeneration || !riskAssessmentComplete || riskDraftChanged} onClick={queueGeneration}>
                            {queueingGeneration ? 'Queueing...' : reportStatus === 'failed' ? 'Retry Report' : 'Generate Report'}
                        </Button>
                    ) : null}
                    <Button onClick={() => setWorkbench(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                fullScreen
                open={Boolean(collaboraSession)}
                onClose={() => setCollaboraSession(null)}
                PaperProps={{ sx: { background: '#f3f3f3' } }}
            >
                <Box sx={{ height: '100vh', display: 'grid', gridTemplateRows: 'auto 1fr', minHeight: 0 }}>
                    <Box sx={{ px: 1.5, py: 0.75, background: '#f7f7f7', color: '#1f2933', borderBottom: '1px solid #c8c8c8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25 }} noWrap>
                                Monsurv Report Editor — {workbench?.item?.deal_reference}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#5f6368' }} noWrap>
                                {issueDocumentName || 'Feasibility working report'}
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setCollaboraSession(null)}
                            sx={{ minHeight: 28, color: '#1f2933', borderColor: '#b8b8b8', background: '#fff', textTransform: 'none', fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}
                        >
                            Close
                        </Button>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '348px minmax(0, 1fr)' }, gap: 0, minHeight: 0 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                height: '100%',
                                minHeight: 0,
                                overflow: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 0,
                                borderTop: 0,
                                borderBottom: 0,
                                borderLeft: 0,
                                borderColor: '#c8c8c8',
                                background: '#f7f7f7'
                            }}
                        >
                            <Stack spacing={0} sx={{ minHeight: '100%', width: '100%' }}>
                                <Box sx={{ px: 1.25, height: 35, borderBottom: '1px solid #005f90', background: '#006AA0', display: 'flex', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#ffffff' }}>
                                        Monsurv Actions
                                    </Typography>
                                </Box>
                                <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid #d6d6d6', background: '#eeeeee', color: '#202124' }}>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>PDF and Issue</Typography>
                                </Box>
                                <Box sx={{ p: 1.25, display: 'grid', gap: 1 }}>
                                    <Typography sx={{ fontSize: 11.5, color: '#5f6368', lineHeight: 1.35 }}>
                                        Save the working report in Collabora before issuing.
                                    </Typography>
                                    <Typography sx={{ fontSize: 12.5, color: '#202124', fontWeight: 700, overflowWrap: 'anywhere' }}>
                                        {workbench?.item?.deal_reference} · {workbench?.item?.deal_name}
                                    </Typography>
                                    <Box>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            startIcon={<PublishIcon />}
                                            onClick={() => { setIssueError(''); setIssueDialogOpen(true); }}
                                            sx={{ minHeight: 30, textTransform: 'none', fontSize: 12.5, fontWeight: 700 }}
                                        >
                                            Issue Report
                                        </Button>
                                    </Box>
                                </Box>
                            </Stack>
                        </Paper>

                        <Box sx={{ minHeight: { xs: '52vh', lg: 0 }, border: 0, overflow: 'hidden', background: '#fff' }}>
                            {collaboraSession?.launch_form?.action ? (
                                <>
                                    <Box component="form" ref={collaboraFormRef} action={collaboraSession.launch_form.action} method="post" target={collaboraFrameNameRef.current} sx={{ display: 'none' }}>
                                        <input type="hidden" name="access_token" value={collaboraSession.launch_form.fields.access_token} readOnly />
                                        <input type="hidden" name="access_token_ttl" value={collaboraSession.launch_form.fields.access_token_ttl} readOnly />
                                        <input type="hidden" name="postmessage_origin" value={window.location.origin} readOnly />
                                        <input type="hidden" name="permission" value="edit" readOnly />
                                    </Box>
                                    <Box
                                        component="iframe"
                                        ref={collaboraFrameRef}
                                        name={collaboraFrameNameRef.current}
                                        title="Feasibility report editor"
                                        onLoad={() => collaboraFrameRef.current?.contentWindow?.postMessage(JSON.stringify({ MessageId: 'Host_PostmessageReady' }), new URL(collaboraSession.launch_form.action).origin)}
                                        sx={{ display: 'block', width: '100%', height: '100%', minHeight: '52vh', border: 0 }}
                                    />
                                </>
                            ) : null}
                        </Box>
                    </Box>
                </Box>
            </Dialog>

            <Dialog open={issueDialogOpen} onClose={issuingReport ? undefined : () => setIssueDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Issue Feasibility Report</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Document name"
                            value={issueDocumentName}
                            onChange={(event) => setIssueDocumentName(event.target.value)}
                            inputProps={{ maxLength: 140 }}
                            helperText="The report will be issued as a PDF."
                        />
                        {issueError ? <Alert severity="error">{issueError}</Alert> : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button disabled={issuingReport} onClick={() => setIssueDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" startIcon={<PublishIcon />} disabled={issuingReport || !issueDocumentName.trim()} onClick={issueReport}>
                        {issuingReport ? 'Issuing...' : 'Confirm Issue'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
